/**
 * Template Sidebar Component
 * Displays the list of available export templates with selection and creation options
 */

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import IconProvider from '@/components/ui/IconProvider';
import { ExportTemplate } from '@/types/exportTemplates';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';

interface TemplateSidebarProps {
  templates: ExportTemplate[];
  selectedTemplateId: string | null;
  activeTemplateId: string | null;
  onSelectTemplate: (id: string) => void;
  onCreateCustom: () => void;
  canCreateCustom: boolean;
}

export const TemplateSidebar = ({
  templates,
  selectedTemplateId,
  activeTemplateId,
  onSelectTemplate,
  onCreateCustom,
  canCreateCustom
}: TemplateSidebarProps) => {
  const { t } = usePlatformTranslation();

  return (
    <div className="col-span-3 space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold">
          {t('platform.settings.exporttemplatessection.templates')}
        </Label>
        <Button
          onClick={onCreateCustom}
          size="sm"
          variant="outline"
          disabled={!canCreateCustom}
          className="gap-1"
        >
          <IconProvider iconstring="fa-plus" size={12} />
          New
        </Button>
      </div>
      
      <div className="space-y-2">
        {templates.map(template => (
          <div
            key={template.id}
            className={`p-3 border rounded cursor-pointer transition-colors ${
              selectedTemplateId === template.id
                ? 'bg-primary/10 border-primary'
                : 'bg-card border-border hover:bg-accent'
            } ${activeTemplateId === template.id ? 'ring-2 ring-primary' : ''}`}
            onClick={() => onSelectTemplate(template.id)}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">
                  {template.name}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {template.platform.toUpperCase()}
                  {template.isPreset && ' • Preset'}
                </div>
              </div>
              {activeTemplateId === template.id && (
                <IconProvider
                  iconstring="fa-check-circle"
                  size={14}
                  className="text-primary flex-shrink-0"
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
